import { useState, useEffect, useCallback } from 'react';

interface Notice {
  id: string;
  title: string;
  content: string;
  created_at: string;
}

interface NoticeCarouselProps {
  notices: Notice[];
  autoPlayMs?: number;
}

interface NoticeCarouselAdminProps {
  notices: Notice[];
  onEdit: (notice: Notice) => void;
  onDelete: (id: string) => void;
}

/** 공지사항 캐러셀 (고객용 - 읽기 전용) */
export function NoticeCarousel({ notices, autoPlayMs = 5000 }: NoticeCarouselProps) {
  const [index, setIndex] = useState(0);

  const next = useCallback(() => setIndex(i => (i + 1) % notices.length), [notices.length]);
  const prev = useCallback(() => setIndex(i => (i - 1 + notices.length) % notices.length), [notices.length]);

  useEffect(() => {
    if (notices.length <= 1) return;
    const timer = setInterval(next, autoPlayMs);
    return () => clearInterval(timer);
  }, [notices.length, autoPlayMs, next]);

  if (notices.length === 0) return null;
  const notice = notices[index];

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex items-stretch min-h-[120px]">
        {/* 이전 */}
        {notices.length > 1 && (
          <button onClick={prev} className="px-3 hover:bg-gray-50 transition-colors text-gray-400 hover:text-gray-600 shrink-0">
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
        )}
        {/* 내용 */}
        <div className="flex-1 py-5 px-4 flex flex-col">
          <h3 className="font-bold text-gray-900 text-center">{notice.title}</h3>
          <p className="text-sm text-gray-600 mt-2 text-center whitespace-pre-wrap flex-1">{notice.content}</p>
          <div className="flex items-center justify-center gap-2 mt-3">
            {notices.map((_, i) => (
              <button key={i} onClick={() => setIndex(i)}
                className={`w-2 h-2 rounded-full transition-colors ${i === index ? 'bg-gray-800' : 'bg-gray-300'}`} />
            ))}
          </div>
        </div>
        {/* 다음 */}
        {notices.length > 1 && (
          <button onClick={next} className="px-3 hover:bg-gray-50 transition-colors text-gray-400 hover:text-gray-600 shrink-0">
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        )}
      </div>
    </div>
  );
}

/** 공지사항 캐러셀 (관리자용 - 수정/삭제 가능) */
export function NoticeCarouselAdmin({ notices, onEdit, onDelete }: NoticeCarouselAdminProps) {
  const [index, setIndex] = useState(0);

  // 삭제 후 인덱스 보정
  useEffect(() => {
    if (index >= notices.length && notices.length > 0) setIndex(notices.length - 1);
  }, [notices.length, index]);

  const next = useCallback(() => setIndex(i => (i + 1) % notices.length), [notices.length]);
  const prev = useCallback(() => setIndex(i => (i - 1 + notices.length) % notices.length), [notices.length]);

  if (notices.length === 0) return null;
  const notice = notices[index];

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
      <div className="flex items-stretch min-h-[140px]">
        {/* 이전 */}
        {notices.length > 1 && (
          <button onClick={prev} className="px-4 hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600 shrink-0 border-r border-slate-200">
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
        )}
        {/* 내용 */}
        <div className="flex-1 py-4 px-5 flex flex-col">
          <div className="bg-purple-50 rounded-lg px-4 py-2 mb-3">
            <h4 className="font-bold text-slate-900 text-center">{notice.title}</h4>
          </div>
          <p className="text-sm text-slate-600 whitespace-pre-wrap text-center flex-1">{notice.content}</p>
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-slate-400">
              {new Date(notice.created_at).toLocaleDateString('ko-KR')} · {index + 1}/{notices.length}
            </span>
            <div className="flex items-center gap-3">
              {/* 인디케이터 */}
              <div className="flex gap-1.5">
                {notices.map((_, i) => (
                  <button key={i} onClick={() => setIndex(i)}
                    className={`w-2 h-2 rounded-full transition-colors ${i === index ? 'bg-purple-500' : 'bg-slate-300'}`} />
                ))}
              </div>
              {/* 수정/삭제 */}
              <div className="flex gap-1 ml-2">
                <button onClick={() => onEdit(notice)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="수정">
                  <i className="ri-edit-line"></i>
                </button>
                <button onClick={() => onDelete(notice.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="삭제">
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* 다음 */}
        {notices.length > 1 && (
          <button onClick={next} className="px-4 hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600 shrink-0 border-l border-slate-200">
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        )}
      </div>
    </div>
  );
}
